import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/core/lib/utils';
import { useTaskDialogStore } from '../../hooks/useTaskDialogStore';
import type { TaskTreeNode } from '../../types';

interface TaskDialogSidebarProps {
  tree: TaskTreeNode | null;
  isLoading: boolean;
}

interface TreeNodeItemProps {
  node: TaskTreeNode;
  depth: number;
  activeTaskId: string | null;
  onSelect: (id: string) => void;
}

function TreeNodeItem({ node, depth, activeTaskId, onSelect }: TreeNodeItemProps) {
  if (!node.id) return null;
  const children = node.children ?? [];

  return (
    <>
      <li>
        <button
          type="button"
          onClick={() => onSelect(node.id!)}
          data-testid={`tasks-sidebar-child-${node.id}`}
          style={{ paddingLeft: `${8 + depth * 12}px` }}
          className={cn(
            'w-full rounded py-1 pr-2 text-left text-xs truncate transition-colors hover:bg-accent',
            activeTaskId === node.id && 'bg-accent font-medium',
          )}
        >
          {node.title ?? '—'}
        </button>
      </li>
      {children.map((child) => (
        <TreeNodeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          activeTaskId={activeTaskId}
          onSelect={onSelect}
        />
      ))}
    </>
  );
}

export function TaskDialogSidebar({ tree, isLoading }: TaskDialogSidebarProps) {
  const { t } = useTranslation('tasks');
  const pushTaskId = useTaskDialogStore((s) => s.pushTaskId);
  const toggleSidebar = useTaskDialogStore((s) => s.toggleSidebar);
  const activeTaskId = useTaskDialogStore((s) => s.activeTaskId);

  return (
    <aside
      data-testid="tasks-dialog-sidebar"
      aria-label={t('dialog.sidebarTitle')}
      className="relative flex flex-col gap-4 overflow-y-auto p-4 text-sm"
    >
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 h-6 w-6"
        onClick={toggleSidebar}
        aria-label={t('dialog.sidebarToggleAria')}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
      <div>
        <h4 className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {t('dialog.sidebarChildren')}
        </h4>
        {isLoading ? (
          <div className="space-y-1.5">
            {[1, 2].map((i) => (
              <div key={i} className="h-6 animate-pulse rounded bg-muted" />
            ))}
          </div>
        ) : !tree ? (
          <p className="text-xs text-muted-foreground">{t('dialog.sidebarEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            <TreeNodeItem
              node={tree}
              depth={0}
              activeTaskId={activeTaskId}
              onSelect={(id) => pushTaskId(id)}
            />
          </ul>
        )}
      </div>
    </aside>
  );
}
